import { useAssistantState } from "@assistant-ui/react";
import {
  Box,
  Flex,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import React, { FC, useCallback, useEffect, useMemo, useRef } from "react";
import Select from "react-select";

import { Button, RangeSlider } from "../../../../components";
import { formatDuration, formatISODate } from "../../../../utils/datetime";
import {
  AnalyticsDimension,
  CustomInsightsInterviewPreview,
  FilterValue,
  useAnalyticsFilterValuesQuery,
  useCustomInsightsInterviewPreviewQuery,
} from "../../../graphql";
import AnalyticsDateSelect from "../AnalyticsDateSelect";
import { AnalyticsSelectDropdownIndicator } from "../AnalyticsSelectDropdownIndicator";
import type { PositionOptions } from "../skills-report/builder/SkillsReportPositionSelect";
import SkillsReportPositionSelect from "../skills-report/builder/SkillsReportPositionSelect";
import { useAnalyticsSelectTheme } from "../useAnalyticsSelectTheme";
import { useCustomInsightsChat } from "./CustomInsightsChatProvider";
import type {
  DateRangeState,
  DurationRangeState,
  InterviewTypeFilter,
  MaxInterviewCountOption,
} from "./CustomInsightsFiltersProvider";
import { useCustomInsightsFiltersContext } from "./CustomInsightsFiltersProvider";
import { CustomInsightsFilterSummary } from "./CustomInsightsFilterSummary";
import { DataAvailabilityIndicator } from "./DataAvailabilityIndicator";

const CANDIDATE_OUTCOME_OPTIONS: FilterValue[] = [
  { id: "HIRED", value: "HIRED", label: "Hired" },
  { id: "REJECTED", value: "REJECTED", label: "Rejected" },
  { id: "ACTIVE", value: "ACTIVE", label: "Active" },
];

const thBaseStyle = {
  bg: "gray.50",
  fontSize: "11.67px",
  fontWeight: "medium",
  lineHeight: "16px",
  color: "gray.600",
  textTransform: "none" as const,
  letterSpacing: "normal",
  h: "36px",
  py: "8px",
};

const tdBaseStyle = {
  fontSize: "14px",
  lineHeight: "20px",
  color: "gray.800",
  h: "48px",
  py: "12px",
};

const DATE_FORMAT_OPTS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

type PreviewColumn = {
  key: string;
  label: string;
  sizing?: { w?: string; minW?: string };
  render: (interview: CustomInsightsInterviewPreview) => React.ReactNode;
};

const PREVIEW_COLUMNS: PreviewColumn[] = [
  {
    key: "date",
    label: "Date",
    sizing: { w: "112px", minW: "112px" },
    render: (i) =>
      i.startTime
        ? new Date(i.startTime).toLocaleDateString("en-US", DATE_FORMAT_OPTS)
        : "—",
  },
  {
    key: "interview",
    label: "Interview",
    render: (i) => i.callName || "—",
  },
  {
    key: "position",
    label: "Position",
    render: (i) => i.positionName || "—",
  },
  {
    key: "candidate",
    label: "Candidate",
    sizing: { w: "108px", minW: "108px" },
    render: (i) => i.candidateName,
  },
];

export type CustomInsightsFilterAndPreviewProps = {
  onApply?: () => void;
  /** Controlled open state. When provided with onOpenChange, the parent controls expand/collapse. */
  isOpen?: boolean;
  /** Called when the filters pane is opened or closed. Use with isOpen for controlled mode. */
  onOpenChange?: (isOpen: boolean) => void;
};

const CollapsedCustomInsightsFilterAndPreview: FC<{
  onOpen: () => void;
  previewInterviews: Parameters<
    typeof DataAvailabilityIndicator
  >[0]["interviews"];
  isPreviewLoading: boolean;
}> = ({ onOpen, previewInterviews, isPreviewLoading }) => {
  const isRunning = useAssistantState((s) => s.thread.isRunning);
  const showNoPreviewData = !isPreviewLoading && previewInterviews.length === 0;
  const {
    callNameSearch,
    dateRange,
    selectedPositions,
    selectedInterviewers,
    selectedDepartments,
    selectedStages,
    selectedLocations,
    selectedCandidateOutcomes,
    durationRange,
    interviewType,
    maxInterviewCount,
  } = useCustomInsightsFiltersContext();
  return (
    <Flex w="100%" align="flex-start" gap="2" px={8} py="17px">
      <Text
        fontSize="xs"
        fontWeight="semibold"
        color="gray.500"
        flexShrink={0}
        pt="4px"
      >
        FILTERS
      </Text>
      <Flex flex={1} flexDir="column" gap="2" minW="0">
        <CustomInsightsFilterSummary
          compact
          colorScheme="purple"
          filters={{
            dateRange,
            durationRange,
            interviewType,
            maxInterviewCount,
            selectedPositions,
            selectedInterviewers,
            selectedDepartments,
            selectedStages,
            selectedLocations,
            selectedCandidateOutcomes,
            callNameSearch,
          }}
        />
        <DataAvailabilityIndicator
          interviews={previewInterviews}
          isLoading={isPreviewLoading}
          label="Coverage:"
        />
        {showNoPreviewData && (
          <Text fontSize="12px" lineHeight="16px" color="red.600">
            No matching interviews for these filters. Update them to enable
            chat.
          </Text>
        )}
      </Flex>
      <Button
        variant="outline"
        colorScheme="purple"
        size="sm"
        fontWeight="500"
        onClick={onOpen}
        isDisabled={isRunning}
        flexShrink={0}
      >
        Edit filters
      </Button>
    </Flex>
  );
};

const MAX_INTERVIEW_COUNT_OPTIONS: MaxInterviewCountOption[] = [50, 100, 200];

const CustomInsightsFilterAndPreview: FC<
  CustomInsightsFilterAndPreviewProps
> = ({ onApply, isOpen: isOpenProp, onOpenChange }) => {
  const {
    callNameSearch,
    setCallNameSearch,
    dateRange,
    setDateRange,
    selectedPositions,
    setSelectedPositions,
    selectedInterviewers,
    setSelectedInterviewers,
    selectedDepartments,
    setSelectedDepartments,
    selectedStages,
    setSelectedStages,
    selectedLocations,
    setSelectedLocations,
    selectedCandidateOutcomes,
    setSelectedCandidateOutcomes,
    durationRange,
    setDurationRange,
    interviewType,
    setInterviewType,
    maxInterviewCount,
    setMaxInterviewCount,
    resetFilters,
  } = useCustomInsightsFiltersContext();
  const formMaxW = "341px";
  const [selectTheme, selectStyles] = useAnalyticsSelectTheme();
  const { setPreviewAvailability } = useCustomInsightsChat();
  const internalDisclosure = useDisclosure({ defaultIsOpen: true });
  const isControlled = isOpenProp !== undefined && onOpenChange != null;
  const isOpen = isControlled ? isOpenProp : internalDisclosure.isOpen;
  const onOpen = useCallback(() => {
    if (isControlled) onOpenChange?.(true);
    else internalDisclosure.onOpen();
  }, [isControlled, onOpenChange, internalDisclosure.onOpen]);
  const onClose = useCallback(() => {
    if (isControlled) onOpenChange?.(false);
    else internalDisclosure.onClose();
  }, [isControlled, onOpenChange, internalDisclosure.onClose]);

  const previousFiltersRef = useRef<{
    callNameSearch: string;
    dateRange: DateRangeState;
    selectedPositions: PositionOptions;
    selectedInterviewers: FilterValue[];
    selectedDepartments: FilterValue[];
    selectedStages: FilterValue[];
    selectedLocations: FilterValue[];
    selectedCandidateOutcomes: FilterValue[];
    durationRange: DurationRangeState;
    interviewType: InterviewTypeFilter;
    maxInterviewCount: MaxInterviewCountOption;
  } | null>(null);

  const handleResetToPrevious = useCallback(() => {
    const prev = previousFiltersRef.current;
    if (!prev) return;
    setCallNameSearch(prev.callNameSearch);
    setDateRange(prev.dateRange);
    setSelectedPositions(prev.selectedPositions);
    setSelectedInterviewers(prev.selectedInterviewers);
    setSelectedDepartments(prev.selectedDepartments);
    setSelectedStages(prev.selectedStages);
    setSelectedLocations(prev.selectedLocations);
    setSelectedCandidateOutcomes(prev.selectedCandidateOutcomes);
    setDurationRange(prev.durationRange);
    setInterviewType(prev.interviewType);
    setMaxInterviewCount(prev.maxInterviewCount);
    onClose();
  }, [
    setCallNameSearch,
    setDateRange,
    setSelectedPositions,
    setSelectedInterviewers,
    setSelectedDepartments,
    setSelectedStages,
    setSelectedLocations,
    setSelectedCandidateOutcomes,
    setDurationRange,
    setInterviewType,
    setMaxInterviewCount,
    onClose,
  ]);

  const filtersHaveChanged = useMemo(() => {
    const prev = previousFiltersRef.current;
    if (!prev) return false;
    if (callNameSearch !== prev.callNameSearch) return true;
    if (
      dateRange.start.getTime() !== prev.dateRange.start.getTime() ||
      dateRange.end.getTime() !== prev.dateRange.end.getTime()
    )
      return true;
    if (
      durationRange.minSeconds !== prev.durationRange.minSeconds ||
      durationRange.maxSeconds !== prev.durationRange.maxSeconds
    )
      return true;
    if (interviewType !== prev.interviewType) return true;
    if (maxInterviewCount !== prev.maxInterviewCount) return true;
    if (selectedPositions.length !== prev.selectedPositions.length) return true;
    if (
      selectedPositions.some((p, i) => p?.id !== prev.selectedPositions[i]?.id)
    )
      return true;
    const sameFilterValues = (a: FilterValue[], b: FilterValue[]): boolean =>
      a.length === b.length &&
      a.every((v, i) => (v?.value ?? "") === (b[i]?.value ?? ""));
    if (!sameFilterValues(selectedInterviewers, prev.selectedInterviewers))
      return true;
    if (!sameFilterValues(selectedDepartments, prev.selectedDepartments))
      return true;
    if (!sameFilterValues(selectedStages, prev.selectedStages)) return true;
    if (!sameFilterValues(selectedLocations, prev.selectedLocations))
      return true;
    if (
      !sameFilterValues(
        selectedCandidateOutcomes,
        prev.selectedCandidateOutcomes
      )
    )
      return true;
    return false;
  }, [
    callNameSearch,
    dateRange,
    durationRange,
    interviewType,
    maxInterviewCount,
    selectedPositions,
    selectedInterviewers,
    selectedDepartments,
    selectedStages,
    selectedLocations,
    selectedCandidateOutcomes,
  ]);

  const positionIds = useMemo(
    () => selectedPositions.map((p) => p.id),
    [selectedPositions]
  );
  const positionDisplayTitles = useMemo(
    () => selectedPositions.map((p) => p.displayTitle),
    [selectedPositions]
  );
  const interviewerValues = useMemo(
    () => selectedInterviewers.map((v) => v.value),
    [selectedInterviewers]
  );
  const departmentValues = useMemo(
    () => selectedDepartments.map((v) => v.value),
    [selectedDepartments]
  );
  const stageValues = useMemo(
    () => selectedStages.map((v) => v.value),
    [selectedStages]
  );
  const locationValues = useMemo(
    () => selectedLocations.map((v) => v.value),
    [selectedLocations]
  );
  const candidateOutcomeValues = useMemo(
    () => selectedCandidateOutcomes.map((v) => v.value),
    [selectedCandidateOutcomes]
  );

  const filterQueryVars = useMemo(
    () => ({
      dateRangeStart: formatISODate(dateRange.start),
      dateRangeEnd: formatISODate(dateRange.end),
      positions: positionDisplayTitles,
      interviewers: interviewerValues,
      departments: departmentValues,
      stages: stageValues,
      locations: locationValues,
      secondaryDimension: AnalyticsDimension.None,
    }),
    [
      dateRange,
      positionDisplayTitles,
      interviewerValues,
      departmentValues,
      stageValues,
      locationValues,
    ]
  );

  const interviewerQuery = useAnalyticsFilterValuesQuery({
    variables: {
      ...filterQueryVars,
      primaryDimension: AnalyticsDimension.Interviewer,
    },
  });
  const departmentQuery = useAnalyticsFilterValuesQuery({
    variables: {
      ...filterQueryVars,
      primaryDimension: AnalyticsDimension.Department,
    },
  });
  const stageQuery = useAnalyticsFilterValuesQuery({
    variables: {
      ...filterQueryVars,
      primaryDimension: AnalyticsDimension.JobStage,
    },
  });
  const locationQuery = useAnalyticsFilterValuesQuery({
    variables: {
      ...filterQueryVars,
      primaryDimension: AnalyticsDimension.Location,
    },
  });

  const previewQuery = useCustomInsightsInterviewPreviewQuery({
    variables: {
      dateRangeStart: formatISODate(dateRange.start),
      dateRangeEnd: formatISODate(dateRange.end),
      positions: positionIds.length ? positionIds : [],
      interviewers: interviewerValues.length ? interviewerValues : [],
      departments: departmentValues.length ? departmentValues : [],
      stages: stageValues.length ? stageValues : [],
      locations: locationValues.length ? locationValues : [],
      candidateOutcomes:
        candidateOutcomeValues.length > 0 ? candidateOutcomeValues : undefined,
      durationMinSeconds: durationRange.minSeconds,
      durationMaxSeconds: durationRange.maxSeconds,
      interviewType:
        interviewType != null
          ? interviewType === "screen"
            ? "SCREEN"
            : "CORE"
          : null,
      maxInterviewCount,
      callNameSearch: callNameSearch || undefined,
    },
  });

  const previewPayload = useMemo(() => {
    const data = previewQuery.loading
      ? previewQuery.previousData
      : previewQuery.data;
    return data?.customInsightsInterviewPreview;
  }, [previewQuery.data, previewQuery.previousData, previewQuery.loading]);

  const previewInterviews = previewPayload?.interviews ?? [];
  const totalMatchCount = previewPayload?.totalMatchCount ?? 0;
  const hasPreviewInterviews = previewInterviews.length > 0;
  const showNoPreviewData = !previewQuery.loading && !hasPreviewInterviews;

  useEffect(() => {
    setPreviewAvailability({
      hasPreviewInterviews,
      isPreviewLoading: previewQuery.loading,
    });
  }, [hasPreviewInterviews, previewQuery.loading, setPreviewAvailability]);

  const interviewerOptions =
    (interviewerQuery.loading
      ? interviewerQuery.previousData
      : interviewerQuery.data
    )?.filterValues?.values ?? [];
  const departmentOptions =
    (departmentQuery.loading
      ? departmentQuery.previousData
      : departmentQuery.data
    )?.filterValues?.values ?? [];
  const stageOptions =
    (stageQuery.loading ? stageQuery.previousData : stageQuery.data)
      ?.filterValues?.values ?? [];
  const locationOptions =
    (locationQuery.loading ? locationQuery.previousData : locationQuery.data)
      ?.filterValues?.values ?? [];

  const { container: containerStyles = {}, ...selectStylesRest } = selectStyles;
  const menuPortalTarget = useMemo(() => document.getElementById("root"), []);
  const filterSelectStyleOverrides = useMemo(
    () => ({
      control: (provided: Record<string, unknown>) => ({
        ...provided,
        borderColor: "#D3D8DE",
        borderRadius: "2px",
        minHeight: "32px",
        boxShadow: "none",
        "&:hover": { borderColor: "#D3D8DE" },
      }),
      placeholder: (provided: Record<string, unknown>) => ({
        ...provided,
        color: "#9CA5AE",
        fontSize: "14px",
      }),
      singleValue: (provided: Record<string, unknown>) => ({
        ...provided,
        color: "#393F47",
        fontSize: "14px",
      }),
      valueContainer: (provided: Record<string, unknown>) => ({
        ...provided,
        color: "#393F47",
        fontSize: "14px",
      }),
      dropdownIndicator: (provided: Record<string, unknown>) => ({
        ...provided,
        padding: "0 8px 0 0",
      }),
      indicatorSeparator: () => ({ display: "none" }),
    }),
    []
  );
  const commonSelectStyles = useMemo(
    () => ({
      container: (provided: Record<string, unknown>) => ({
        ...provided,
        flex: 1,
        ...containerStyles,
      }),
      menuPortal: (provided: Record<string, unknown>) => ({
        ...provided,
        zIndex: 20,
      }),
      ...selectStylesRest,
      ...filterSelectStyleOverrides,
    }),
    [containerStyles, filterSelectStyleOverrides, selectStylesRest]
  );
  const selectComponentOverrides = useMemo(
    () => ({ DropdownIndicator: AnalyticsSelectDropdownIndicator }),
    []
  );
  const positionSelectStyleOverrides = filterSelectStyleOverrides;
  const dateSelectStyleOverrides = useMemo(() => {
    const { control, singleValue, dropdownIndicator, indicatorSeparator } =
      filterSelectStyleOverrides;

    return {
      control,
      singleValue,
      dropdownIndicator,
      indicatorSeparator,
    };
  }, [filterSelectStyleOverrides]);

  return (
    <Flex
      flexDir="column"
      bg="white"
      borderBottom="1px solid"
      borderColor="gray.200"
      h={isOpen ? "100%" : undefined}
    >
      {!isOpen && (
        <CollapsedCustomInsightsFilterAndPreview
          onOpen={onOpen}
          previewInterviews={previewInterviews}
          isPreviewLoading={previewQuery.loading}
        />
      )}
      {isOpen && (
        <>
          <Box px={8} py={4} borderBottom="1px solid" borderColor="gray.200">
            <Text
              fontSize="16px"
              fontWeight="semibold"
              lineHeight="24px"
              color="gray.800"
            >
              Filter down to the interviews you care about, then ask questions
              to find patterns.
            </Text>
            <Text fontSize="14px" lineHeight="20px" color="gray.600" mt="2px">
              Smaller, focused datasets surface sharper insights.
            </Text>
          </Box>
          <Flex
            flexDir="row"
            align="stretch"
            w="100%"
            flex={1}
            minH={0}
            overflow="hidden"
          >
            <Flex
              flexDir="column"
              maxW={formMaxW}
              w={formMaxW}
              flexShrink={0}
              overflowY="auto"
            >
              <Flex
                flexDir="row"
                justifyContent="space-between"
                alignItems="center"
                borderBottom="1px solid"
                borderColor="gray.100"
                pl="32px"
                pr="16px"
                py="8px"
              >
                <Text
                  fontSize="12px"
                  fontWeight="semibold"
                  lineHeight="16px"
                  color="gray.500"
                >
                  FILTERS
                </Text>
                <Text
                  fontSize="11.67px"
                  fontWeight="medium"
                  color="gray.200"
                  cursor="pointer"
                  onClick={resetFilters}
                  lineHeight="16px"
                  px="12px"
                  py="3px"
                >
                  Clear
                </Text>
              </Flex>
              <VStack
                spacing="10px"
                align="stretch"
                pl="32px"
                pr="16px"
                py="16px"
              >
                <Input
                  size="sm"
                  placeholder="Interview name keywords"
                  value={callNameSearch}
                  onChange={(e) => setCallNameSearch(e.target.value)}
                  borderColor="gray.200"
                  borderRadius="6px"
                  h="32px"
                  _placeholder={{ color: "gray.400" }}
                />

                <AnalyticsDateSelect
                  direction="column"
                  dateInputStyleProps={{ width: "100%", minW: 0 }}
                  rangeStyleProps={{
                    width: "100%",
                    minW: 0,
                  }}
                  datePickerWrapperClassName="bh-date-picker-flex"
                  customOptionLabel="Custom date range"
                  state={dateRange}
                  onSelect={(val) => {
                    if (val) setDateRange(val);
                  }}
                  selectComponentOverrides={selectComponentOverrides}
                  selectStyleOverrides={dateSelectStyleOverrides}
                />

                <Select
                  styles={commonSelectStyles}
                  theme={selectTheme}
                  menuPortalTarget={menuPortalTarget}
                  components={selectComponentOverrides}
                  placeholder="All departments"
                  options={departmentOptions}
                  value={departmentOptions.filter((o) =>
                    selectedDepartments.some((s) => s.value === o.value)
                  )}
                  onChange={(val) =>
                    setSelectedDepartments(val as FilterValue[])
                  }
                  getOptionLabel={(o: FilterValue) => o.label || o.value}
                  getOptionValue={(o: FilterValue) => o.value}
                  isMulti
                  isClearable
                  isLoading={departmentQuery.loading}
                  noOptionsMessage={() => "No departments found"}
                />

                <Select
                  styles={commonSelectStyles}
                  theme={selectTheme}
                  menuPortalTarget={menuPortalTarget}
                  components={selectComponentOverrides}
                  placeholder="All stages"
                  options={stageOptions}
                  value={stageOptions.filter((o) =>
                    selectedStages.some((s) => s.value === o.value)
                  )}
                  onChange={(val) => setSelectedStages(val as FilterValue[])}
                  getOptionLabel={(o: FilterValue) => o.label || o.value}
                  getOptionValue={(o: FilterValue) => o.value}
                  isMulti
                  isClearable
                  isLoading={stageQuery.loading}
                  noOptionsMessage={() => "No stages found"}
                />

                <SkillsReportPositionSelect
                  dateRange={dateRange}
                  selectedPositions={selectedPositions}
                  setSelectedPositions={setSelectedPositions}
                  menuPortalTarget={menuPortalTarget}
                  placeholder="All positions"
                  selectComponentOverrides={selectComponentOverrides}
                  selectStyleOverrides={positionSelectStyleOverrides}
                />

                <Select
                  styles={commonSelectStyles}
                  theme={selectTheme}
                  menuPortalTarget={menuPortalTarget}
                  components={selectComponentOverrides}
                  placeholder="All interviewers"
                  options={interviewerOptions}
                  value={interviewerOptions.filter((o) =>
                    selectedInterviewers.some((s) => s.value === o.value)
                  )}
                  onChange={(val) =>
                    setSelectedInterviewers(val as FilterValue[])
                  }
                  getOptionLabel={(o: FilterValue) => o.label || o.value}
                  getOptionValue={(o: FilterValue) => o.value}
                  isMulti
                  isClearable
                  isLoading={interviewerQuery.loading}
                  noOptionsMessage={() => "No interviewers found"}
                />

                <Select
                  styles={commonSelectStyles}
                  theme={selectTheme}
                  menuPortalTarget={menuPortalTarget}
                  components={selectComponentOverrides}
                  placeholder="All outcomes"
                  options={CANDIDATE_OUTCOME_OPTIONS}
                  value={CANDIDATE_OUTCOME_OPTIONS.filter((o) =>
                    selectedCandidateOutcomes.some((s) => s.value === o.value)
                  )}
                  onChange={(val) =>
                    setSelectedCandidateOutcomes((val as FilterValue[]) ?? [])
                  }
                  getOptionLabel={(o: FilterValue) => o.label || o.value}
                  getOptionValue={(o: FilterValue) => o.value}
                  isMulti
                  isClearable
                  noOptionsMessage={() => "No options"}
                />

                <Select
                  styles={commonSelectStyles}
                  theme={selectTheme}
                  menuPortalTarget={menuPortalTarget}
                  components={selectComponentOverrides}
                  placeholder="All locations"
                  options={locationOptions}
                  value={locationOptions.filter((o) =>
                    selectedLocations.some((s) => s.value === o.value)
                  )}
                  onChange={(val) => setSelectedLocations(val as FilterValue[])}
                  getOptionLabel={(o: FilterValue) => o.label || o.value}
                  getOptionValue={(o: FilterValue) => o.value}
                  isMulti
                  isClearable
                  isLoading={locationQuery.loading}
                  noOptionsMessage={() => "No locations found"}
                />

                <Select<{ value: string; label: string }, false>
                  styles={commonSelectStyles}
                  theme={selectTheme}
                  menuPortalTarget={menuPortalTarget}
                  components={selectComponentOverrides}
                  placeholder="All interview types"
                  options={[
                    { value: "screen", label: "Screen" },
                    { value: "core", label: "Core" },
                  ]}
                  value={
                    interviewType
                      ? {
                          value: interviewType,
                          label: interviewType === "screen" ? "Screen" : "Core",
                        }
                      : null
                  }
                  onChange={(val) =>
                    setInterviewType(
                      val?.value === "screen"
                        ? "screen"
                        : val?.value === "core"
                        ? "core"
                        : null
                    )
                  }
                  isClearable
                  getOptionLabel={(o) => o.label}
                  getOptionValue={(o) => o.value}
                />

                <Box>
                  <Text
                    fontSize="12px"
                    lineHeight="16px"
                    color="gray.600"
                    mb="8px"
                  >
                    Interview Duration
                  </Text>
                  <Flex
                    justify="space-between"
                    align="center"
                    fontSize="12px"
                    lineHeight="16px"
                    mb="4px"
                    h="16px"
                  >
                    <Text flex={1} color="gray.500">
                      {formatDuration(0)}
                    </Text>
                    <Text
                      fontWeight="medium"
                      color="gray.800"
                      textAlign="center"
                    >
                      {formatDuration(durationRange.minSeconds)} –{" "}
                      {formatDuration(durationRange.maxSeconds)}
                    </Text>
                    <Text flex={1} color="gray.500" textAlign="right">
                      {formatDuration(7200)}
                    </Text>
                  </Flex>
                  <RangeSlider
                    min={0}
                    max={7200}
                    values={{
                      min: durationRange.minSeconds,
                      max: durationRange.maxSeconds,
                    }}
                    onChange={(min, max) =>
                      setDurationRange({ minSeconds: min, maxSeconds: max })
                    }
                    onAfterChange={(min, max) =>
                      setDurationRange({ minSeconds: min, maxSeconds: max })
                    }
                  />
                </Box>

                <Box>
                  <Text
                    fontSize="12px"
                    lineHeight="16px"
                    color="gray.600"
                    mb="4px"
                  >
                    Max interview count
                  </Text>
                  <Flex
                    bg="blackAlpha.100"
                    borderRadius="6px"
                    p="4px"
                    gap="4px"
                  >
                    {MAX_INTERVIEW_COUNT_OPTIONS.map((n) => (
                      <Flex
                        key={n}
                        flex={1}
                        justify="center"
                        align="center"
                        py="4px"
                        px="12px"
                        fontSize="11.67px"
                        lineHeight="16px"
                        cursor="pointer"
                        fontWeight={
                          maxInterviewCount === n ? "semibold" : "medium"
                        }
                        bg={maxInterviewCount === n ? "white" : "transparent"}
                        borderRadius="4px"
                        color="gray.900"
                        onClick={() => setMaxInterviewCount(n)}
                        _hover={{
                          bg:
                            maxInterviewCount === n ? "white" : "blackAlpha.50",
                        }}
                      >
                        {n}
                      </Flex>
                    ))}
                  </Flex>
                </Box>
              </VStack>
            </Flex>
            <Flex
              flexDir="column"
              flex={1}
              minW={0}
              borderLeft="1px solid"
              borderColor="gray.200"
              overflow="hidden"
            >
              <Flex
                align="center"
                justify="space-between"
                borderBottom="1px solid"
                borderColor="gray.100"
                pl="16px"
                pr="32px"
                py="11px"
              >
                <Text
                  fontSize="12px"
                  fontWeight="semibold"
                  lineHeight="16px"
                  color="gray.500"
                >
                  MATCHING INTERVIEWS
                </Text>
                <Text fontSize="12px" lineHeight="16px" color="gray.500">
                  Showing {previewInterviews.length} of {totalMatchCount}{" "}
                  matches
                </Text>
              </Flex>
              <Box
                position="relative"
                flex={1}
                overflowY="auto"
                pl="16px"
                pr="1px"
                pb="16px"
              >
                <Table
                  size="sm"
                  variant="unstyled"
                  opacity={previewQuery.loading ? 0.4 : 1}
                  transition="opacity 0.15s"
                >
                  <Thead position="sticky" top={0} zIndex={1}>
                    <Tr h="16px">
                      {PREVIEW_COLUMNS.map((col) => (
                        <Th key={col.key} p={0} border="none" bg="white" />
                      ))}
                    </Tr>
                    <Tr>
                      {PREVIEW_COLUMNS.map((col, i) => (
                        <Th
                          key={col.key}
                          {...thBaseStyle}
                          {...col.sizing}
                          pl={i === 0 ? "16px" : "8px"}
                          pr={i === PREVIEW_COLUMNS.length - 1 ? "16px" : "8px"}
                          borderTopLeftRadius={i === 0 ? "8px" : undefined}
                          borderTopRightRadius={
                            i === PREVIEW_COLUMNS.length - 1 ? "8px" : undefined
                          }
                        >
                          {col.label}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {previewInterviews.map((interview) => (
                      <Tr
                        key={interview.callId}
                        borderBottom="1px solid"
                        borderColor="gray.100"
                      >
                        {PREVIEW_COLUMNS.map((col, i) => (
                          <Td
                            key={col.key}
                            {...tdBaseStyle}
                            {...col.sizing}
                            pl={i === 0 ? "16px" : "8px"}
                            pr={
                              i === PREVIEW_COLUMNS.length - 1 ? "16px" : "8px"
                            }
                          >
                            {col.render(interview)}
                          </Td>
                        ))}
                      </Tr>
                    ))}
                    {!previewQuery.loading && previewInterviews.length === 0 && (
                      <Tr>
                        <Td colSpan={4} textAlign="center" borderBottom="none">
                          No matching interviews for these filters. Update them
                          to enable chat.
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
                {previewQuery.loading && (
                  <Flex
                    position="absolute"
                    inset={0}
                    align="center"
                    justify="center"
                    zIndex={2}
                  >
                    <Spinner size="sm" color="blue.500" />
                  </Flex>
                )}
              </Box>
            </Flex>
          </Flex>
          <Flex
            flexShrink={0}
            zIndex={10}
            bg="white"
            borderTop="1px solid"
            borderColor="gray.200"
            boxShadow="0px -4px 6px 0px rgba(0,0,0,0.05), 0px -2px 4px 0px rgba(0,0,0,0.03)"
            px="32px"
            py="16px"
            h="82px"
            align="flex-start"
            justify="space-between"
          >
            <Flex
              flex={1}
              gap="8px"
              minW={0}
              alignSelf="stretch"
              align="flex-start"
            >
              <Flex h="100%" pt="3px" flexShrink={0}>
                <Text
                  fontSize="12px"
                  fontWeight="semibold"
                  lineHeight="16px"
                  color="gray.500"
                  whiteSpace="nowrap"
                >
                  SELECTED
                </Text>
              </Flex>
              <Flex flex={1} flexDir="column" gap="4px" minW={0}>
                <CustomInsightsFilterSummary
                  compact
                  nonDefaultOnly
                  colorScheme="purple"
                  filters={{
                    dateRange,
                    durationRange,
                    interviewType,
                    maxInterviewCount,
                    selectedPositions,
                    selectedInterviewers,
                    selectedDepartments,
                    selectedStages,
                    selectedLocations,
                    selectedCandidateOutcomes,
                    callNameSearch,
                  }}
                />
                <DataAvailabilityIndicator
                  interviews={previewInterviews}
                  isLoading={previewQuery.loading}
                  label="Coverage:"
                />
                {showNoPreviewData && (
                  <Text fontSize="12px" lineHeight="16px" color="red.600">
                    No matching interviews for these filters. Update them to
                    enable chat.
                  </Text>
                )}
              </Flex>
            </Flex>
            <Flex gap="16px" align="center" flexShrink={0} alignSelf="center">
              {previousFiltersRef.current !== null && (
                <Button
                  variant="outline"
                  size="sm"
                  fontWeight="500"
                  onClick={handleResetToPrevious}
                >
                  Cancel
                </Button>
              )}
              <Button
                size="sm"
                fontWeight="500"
                bg="accent.600"
                color="white"
                borderColor="accent.600"
                borderWidth="1px"
                borderRadius="6px"
                _hover={{ bg: "accent.700" }}
                _active={{ bg: "accent.800" }}
                onClick={() => {
                  if (onApply) {
                    onApply();
                  }
                  onClose();

                  previousFiltersRef.current = {
                    callNameSearch,
                    dateRange: { ...dateRange },
                    selectedPositions: [...selectedPositions],
                    selectedInterviewers: [...selectedInterviewers],
                    selectedDepartments: [...selectedDepartments],
                    selectedStages: [...selectedStages],
                    selectedLocations: [...selectedLocations],
                    selectedCandidateOutcomes: [...selectedCandidateOutcomes],
                    durationRange: {
                      minSeconds: durationRange.minSeconds,
                      maxSeconds: durationRange.maxSeconds,
                    },
                    interviewType,
                    maxInterviewCount,
                  };
                }}
                disabled={
                  previewQuery.loading ||
                  showNoPreviewData ||
                  (previousFiltersRef.current !== null && !filtersHaveChanged)
                }
              >
                Apply filters and explore
              </Button>
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default CustomInsightsFilterAndPreview;
